// Industry API Service for Frontend Integration
// Uses the NEW dedicated industry endpoints with pure BLS crosswalk data
// NO business data included - separate from SOC API

import { getApiBaseUrl, getApiKey } from "../utils/config";

export interface IndustryItem {
  soc_code?: string;
  industry_code: string;
  industry_title: string;
  industry_type: string;
  industry_sort: number;
  display_level: number;
  employment_2024: number;
  projected_employment_2034: number;
  employment_change: number;
  employment_percent_change: number;
  percent_of_industry_2024: number;
  percent_of_occupation_2024: number;
  projected_percent_of_industry_2034: number;
  projected_percent_of_occupation_2034: number;
  // Legacy fields for backward compatibility
  employment_2023?: number;
  projected_employment_2033?: number;
  percent_of_industry_2023?: number;
  percent_of_occupation_2023?: number;
}

export interface IndustryListItem {
  industry_code: string;
  industry_title: string;
  industry_type: string;
  industry_sort: number;
  display_level: number;
  total_employment: number;
  total_projected_employment: number;
  soc_codes_count: number;
  avg_employment_change: number;
}

export interface IndustryApiResponse {
  success: boolean;
  industries: IndustryItem[];
  count: number;
  total_count: number;
  pagination: {
    limit: number;
    skip: number;
    has_more: boolean;
    total_pages: number;
    current_page: number;
  };
  filters_applied: {
    sort_by?: string;
    industry_type?: string;
    min_employment?: number;
  };
  data_source: {
    collection: string;
    type: string;
    description: string;
  };
  timestamp: string;
  soc_code?: string;
  message?: string;
}

export interface IndustryListResponse {
  success: boolean;
  industries: IndustryListItem[];
  count: number;
  total_unique_industries: number;
  pagination: {
    limit: number;
    skip: number;
    has_more: boolean;
    total_pages: number;
    current_page: number;
  };
  filters_applied: {
    search?: string;
    sort_by?: string;
    industry_type?: string;
  };
  data_source: {
    collection: string;
    type: string;
    description: string;
  };
  timestamp: string;
}

export interface IndustryStatsResponse {
  success: boolean;
  statistics: {
    total_crosswalk_records: number;
    unique_industries: number;
    unique_soc_codes: number;
    industry_type_distribution: Array<{
      _id: string;
      count: number;
    }>;
    top_industries_by_employment: Array<{
      industry_code: string;
      industry_title: string;
      total_employment: number;
    }>;
  };
  data_source: {
    collection: string;
    type: string;
    description: string;
  };
  timestamp: string;
}

class IndustryApiService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Get API configuration dynamically
    const baseUrl = getApiBaseUrl();
    const apiKey = getApiKey();
    
    // Ensure proper URL construction without double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${baseUrl}${cleanEndpoint}`;
    
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${apiKey}`,
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Get all industries from pure BLS crosswalk data
  async getAllIndustries(params: {
    limit?: number;
    skip?: number;
    search?: string;
    sort_by?: string;
    industry_type?: string;
  } = {}): Promise<IndustryListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.skip) queryParams.append('skip', params.skip.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.industry_type) queryParams.append('industry_type', params.industry_type);
    
    // Add cache buster
    queryParams.append('_cache', Date.now().toString());

    return this.request<IndustryListResponse>(`/api/industries?${queryParams}`);
  }

  // Get industries for a specific SOC code using pure BLS crosswalk data
  async getIndustriesBySOC(
    socCode: string,
    params: {
      limit?: number;
      skip?: number;
      sort_by?: string;
      industry_type?: string;
      min_employment?: number;
    } = {}
  ): Promise<IndustryApiResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.skip) queryParams.append('skip', params.skip.toString());
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.industry_type) queryParams.append('industry_type', params.industry_type);
    if (params.min_employment) queryParams.append('min_employment', params.min_employment.toString());
    
    // Add cache buster
    queryParams.append('_cache', Date.now().toString());

    return this.request<IndustryApiResponse>(`/api/industries/${encodeURIComponent(socCode)}?${queryParams}`);
  }

  // Get industry statistics from pure BLS crosswalk data
  async getIndustryStats(): Promise<IndustryStatsResponse> {
    const queryParams = new URLSearchParams();
    // Add cache buster
    queryParams.append('_cache', Date.now().toString());

    return this.request<IndustryStatsResponse>(`/api/industries/stats?${queryParams}`);
  }

  // Search industries by title
  async searchIndustries(
    searchTerm: string,
    params: {
      limit?: number;
      skip?: number;
      sort_by?: string;
      industry_type?: string;
    } = {}
  ): Promise<IndustryListResponse> {
    return this.getAllIndustries({
      ...params,
      search: searchTerm
    });
  }

  // Get unique industry types for filtering
  async getIndustryTypes(): Promise<string[]> {
    try {
      const stats = await this.getIndustryStats();
      return stats.statistics.industry_type_distribution.map(item => item._id);
    } catch (error) {
      console.error('Failed to fetch industry types:', error);
      // Fallback to known industry types
      return ['Line Item', 'Summary'];
    }
  }

  // Format industry data for display
  formatIndustryForDisplay(industry: IndustryItem): {
    title: string;
    code: string;
    type: string;
    employment: string;
    projectedEmployment: string;
    employmentChange: string;
    growthRate: string;
    percentOfIndustry: string;
    percentOfOccupation: string;
  } {
    return {
      title: industry.industry_title,
      code: industry.industry_code,
      type: industry.industry_type,
      employment: industry.employment_2023?.toLocaleString() || '0',
      projectedEmployment: industry.projected_employment_2033?.toLocaleString() || '0',
      employmentChange: industry.employment_change?.toLocaleString() || '0',
      growthRate: `${(industry.employment_percent_change || 0).toFixed(1)}%`,
      percentOfIndustry: `${(industry.percent_of_industry_2023 || 0).toFixed(2)}%`,
      percentOfOccupation: `${(industry.percent_of_occupation_2023 || 0).toFixed(2)}%`
    };
  }

  // Format industry list data for display
  formatIndustryListForDisplay(industry: IndustryListItem): {
    title: string;
    code: string;
    type: string;
    totalEmployment: string;
    projectedEmployment: string;
    socCodesCount: string;
    avgGrowthRate: string;
  } {
    return {
      title: industry.industry_title,
      code: industry.industry_code,
      type: industry.industry_type,
      totalEmployment: industry.total_employment?.toLocaleString() || '0',
      projectedEmployment: industry.total_projected_employment?.toLocaleString() || '0',
      socCodesCount: industry.soc_codes_count?.toString() || '0',
      avgGrowthRate: `${(industry.avg_employment_change || 0).toFixed(1)}%`
    };
  }
}

export const industryApiService = new IndustryApiService();
export default industryApiService;
